import * as React from 'react';
import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import type EventEmitter from 'eventemitter3';
import SearchSuggestionsEntry from './SearchSuggestionsEntry';
import type {SearchConfig, SearchField, QueryToken} from './types';
import {getMatchingFields, completeFieldQuery} from './fields';
const EMPTY_ARRAY: Array<SearchField & {id?: string}> = [];
type OwnProps = {
	config: SearchConfig<any>;
	query: string;
	queryTokens: Array<QueryToken> | null | undefined;
	changeQuery: (query: string) => void;
	keyboardEventEmitter: EventEmitter;
	submitted: boolean;
};
type Props = OwnProps;
const SearchSuggestions: React.FC<Props> = ({config, query, queryTokens, changeQuery, keyboardEventEmitter}) => {
	const [matchingFields, setMatchingFields] = React.useState<Array<SearchField & {id?: string}>>([]);
	const [matchingItems, setMatchingItems] = React.useState<Array<SearchField & {id?: string}>>(EMPTY_ARRAY);
	const [isFetchingItems, setIsFetchingItems] = React.useState(false);
	const [totalItemCount, setTotalItemCount] = React.useState<number | null | undefined>(null);
	const [selectedIndex, setSelectedIndex] = React.useState<number | null | undefined>(null);
	const isMountedRef = React.useRef(true);

	const updateFieldsAndItems = React.useCallback(() => {
		const {search, fields, suggestions} = config;
		const newMatchingFields = getMatchingFields(query, queryTokens, fields);
		setMatchingFields(newMatchingFields);
		setSelectedIndex(null);

		if (suggestions && queryTokens && queryTokens.length) {
			const {maxEntries, sort} = suggestions;
			const fetchSize = maxEntries - newMatchingFields.length;

			if (fetchSize > 0) {
				setIsFetchingItems(true);
				setSelectedIndex(null);

				const paginationSettings = {
					page: 0,
					size: fetchSize,
					sort
				};

				search(query, paginationSettings).then((result) => {
					if (isMountedRef.current) {
						setMatchingItems(result.content);
						setTotalItemCount(result.totalElements);
						setIsFetchingItems(false);
					}
				});
			} else {
				setMatchingItems(EMPTY_ARRAY);
				setTotalItemCount(0);
				setSelectedIndex(null);
			}
		} else {
			setMatchingItems(EMPTY_ARRAY);
			setTotalItemCount(0);
			setSelectedIndex(null);
		}
	}, [config, query, queryTokens]);

	const arrowDownListener = React.useCallback(() => {
		setSelectedIndex((prevIndex) => {
			const nextIndex = prevIndex == null ? 0 : prevIndex + 1;
			return nextIndex < matchingFields.length + matchingItems.length ? nextIndex : prevIndex;
		});
	}, [matchingFields.length, matchingItems.length]);

	const arrowUpListener = React.useCallback(() => {
		setSelectedIndex((prevIndex) => {
			return prevIndex == null || prevIndex === 0 ? null : prevIndex - 1;
		});
	}, []);

	// Setup event listeners
	React.useEffect(() => {
		keyboardEventEmitter.addListener('ArrowDown', arrowDownListener);
		keyboardEventEmitter.addListener('ArrowUp', arrowUpListener);

		return () => {
			keyboardEventEmitter.removeListener('ArrowDown', arrowDownListener);
			keyboardEventEmitter.removeListener('ArrowUp', arrowUpListener);
			keyboardEventEmitter.removeAllListeners('Enter');
			isMountedRef.current = false;
		};
	}, [keyboardEventEmitter, arrowDownListener, arrowUpListener]);

	// Update fields and items when query changes
	React.useEffect(() => {
		updateFieldsAndItems();
	}, [updateFieldsAndItems]);

	// Handle Enter key based on selected index
	React.useEffect(() => {
		keyboardEventEmitter.removeAllListeners('Enter');

		if (selectedIndex != null && matchingFields) {
			if (selectedIndex < matchingFields.length) {
				keyboardEventEmitter.addListener('Enter', () => {
					changeQuery(completeFieldQuery(query, matchingFields[selectedIndex]));
				});
			} else if (matchingItems && selectedIndex < matchingFields.length + matchingItems.length) {
				keyboardEventEmitter.addListener('Enter', () => {
					if (config.suggestions) {
						config.suggestions.onAccept(matchingItems[selectedIndex - matchingFields.length]);

						if (document.activeElement) {
							(document.activeElement as HTMLDivElement).blur();
						}
					}
				});
			}
		}
	}, [selectedIndex, matchingFields, matchingItems, keyboardEventEmitter, query, changeQuery, config.suggestions]);

	const show = matchingFields.length || matchingItems.length || isFetchingItems;

	return (
		<span
			className="search-suggest-box"
			style={{
				display: show ? 'block' : 'none'
			}}>
			{matchingFields.length > 0 && (
				<ul className="section filter">
					{matchingFields.map((field: SearchField, index) => (
						<SearchSuggestionsEntry
							key={index}
							label={`${field.key}:`}
							onAccept={() => changeQuery(completeFieldQuery(query, field))}
							selected={index === selectedIndex}
						/>
					))}
				</ul>
			)}
			{config.suggestions && queryTokens && (matchingItems.length || isFetchingItems) && (
				<ul className="section filter">
					{isFetchingItems && (
						<li className="section-item">
							<Trans i18nKey="search.suggestions.loading">Loading...</Trans>
						</li>
					)}
					{!isFetchingItems &&
						matchingItems.length &&
						matchingItems.map((item, index) => (
							<SearchSuggestionsEntry
								key={item.id || item.key}
								label={config.suggestions?.print(item, queryTokens) || ''}
								onAccept={() => config.suggestions?.onAccept(item)}
								selected={index + matchingFields.length === selectedIndex}
							/>
						))}
				</ul>
			)}
			{matchingItems.length > 0 &&
				totalItemCount &&
				totalItemCount > matchingItems.length &&
				!isFetchingItems && (
					<span className="section footer">
						<Trans
							i18nKey="search.suggestions.moreMatches"
							values={{
								count: totalItemCount - matchingItems.length
							}}>
							<>
								{{
									count: totalItemCount - matchingItems.length
								}}{' '}
								more matches
							</>
						</Trans>
					</span>
				)}
		</span>
	);
};

export default SearchSuggestions;
